import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  Settings as SettingsIcon,
  Category as CategoryIcon,
  Analytics as AnalyticsIcon,
  Payment as PaymentIcon,
  Warehouse as WarehouseIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  Webhook as WebhookIcon,
  Add as AddIcon,
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  Sell as SellIcon,
  ShoppingBag as PurchaseIcon,
  Group as PartyIcon,
  Undo as ReturnIcon,
  PostAdd as CreateDocIcon,
  Visibility as VisibilityIcon,
  Contacts as ContactsIcon,
  List as ListIcon,
  Upload as UploadIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  Description as ApiDocsIcon,
  Storefront as CustomerPortalIcon,
} from '@mui/icons-material'
import { MenuItem } from '../types/navigation'

/**
 * Navigation configuration for B2B Document Management System
 * Comprehensive structure with integrated Sales & Purchase workflows
 * Party Management (Customers & Suppliers combined)
 * Document-centric approach with creation and viewing capabilities
 */
export const getNavigationItems = (): MenuItem[] => [
  // ========== MAIN DASHBOARD ==========
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/admin/dashboard',
    permission: 'dashboard.read'
  },

  // ========== SALES WORKFLOW ==========
  {
    text: 'Sales',
    icon: <SellIcon />,
    permission: 'orders.read',
    children: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/sales/dashboard', permission: 'dashboard.read' },
      { text: 'Quotations', icon: <AssignmentIcon />, path: '/admin/documents/estimates', permission: 'orders.read' },
      { text: 'Orders', icon: <ShoppingCartIcon />, path: '/admin/documents/sales-orders', permission: 'orders.read' },
      { text: 'Invoices', icon: <ReceiptIcon />, path: '/admin/documents/invoices', permission: 'orders.read' },
      { text: 'Returns', icon: <ReturnIcon />, path: '/admin/documents/sales-returns', permission: 'orders.read' },
    ],
  },

  // ========== PURCHASE WORKFLOW ==========
  {
    text: 'Purchase',
    icon: <PurchaseIcon />,
    permission: 'orders.read',
    children: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/purchase/dashboard', permission: 'dashboard.read' },
      { text: 'Purchase Orders', icon: <AssignmentIcon />, path: '/admin/purchase/orders', permission: 'orders.read' },
      { text: 'Purchase Bills', icon: <ReceiptIcon />, path: '/admin/purchase/bills', permission: 'orders.read' },
      { text: 'Purchase Returns', icon: <ReturnIcon />, path: '/admin/purchase/returns', permission: 'orders.read' },
    ],
  },

  // ========== PARTY MANAGEMENT (CUSTOMERS & SUPPLIERS) ==========
  {
    text: 'Parties',
    icon: <PartyIcon />,
    permission: 'customers.read',
    children: [
      { text: 'All Parties', icon: <PartyIcon />, path: '/admin/parties', permission: 'customers.read' },
      { text: 'Customer Groups', icon: <CategoryIcon />, path: '/admin/parties/customer-groups', permission: 'customers.read' },
      { text: 'Ledgers', icon: <AccountBalanceIcon />, path: '/admin/parties/ledgers', permission: 'customers.read' },
    ],
  },

  // ========== LEAD MANAGEMENT ==========
  {
    text: 'Leads',
    icon: <ContactsIcon />,
    permission: 'leads.read',
    children: [
      { text: 'All Leads', icon: <ListIcon />, path: '/admin/leads', permission: 'leads.read' },
      { text: 'Import Leads', icon: <UploadIcon />, path: '/admin/leads/import', permission: 'leads.write' },
      { text: 'Segments', icon: <FilterListIcon />, path: '/admin/leads/segments', permission: 'leads.read' },
    ],
  },

  // ========== PRODUCT CATALOG ==========
  {
    text: 'Inventory',
    icon: <InventoryIcon />,
    permission: 'products.read',
    children: [
      { text: 'Products', icon: <InventoryIcon />, path: '/admin/products', permission: 'products.read' },
      { text: 'Catalogues', icon: <CategoryIcon />, path: '/admin/catalogue-management', permission: 'catalogues.read' },
      { text: 'Stock', icon: <WarehouseIcon />, path: '/admin/inventory-management', permission: 'inventory.read' },
      { text: 'Bulk Adjust', icon: <EditIcon />, path: '/admin/inventory/bulk-adjust', permission: 'inventory.write' },
      { text: 'Movement History', icon: <HistoryIcon />, path: '/admin/inventory/movements', permission: 'inventory.read' },
    ],
  },

  // ========== ANALYTICS & REPORTS ==========
  {
    text: 'Analytics',
    icon: <AnalyticsIcon />,
    permission: 'reports.sales',
    children: [
      { text: 'Sales Analytics', icon: <SellIcon />, path: '/admin/analytics/sales', permission: 'reports.sales' },
      { text: 'Customer Analytics', icon: <PartyIcon />, path: '/admin/analytics/customers', permission: 'reports.customers' },
      { text: 'Customer Behavior', icon: <VisibilityIcon />, path: '/admin/analytics/behavior', permission: 'analytics.behavior.read' },
      { text: 'Active Carts', icon: <ShoppingCartIcon />, path: '/admin/carts/active', permission: 'sales.carts.read' },
      { text: 'Custom Reports', icon: <AssessmentIcon />, path: '/admin/reports/custom', permission: 'reports.sales' },
      { text: 'Audit Logs', icon: <HistoryIcon />, path: '/analytics/audit-logs', permission: 'admin.audit.read' },
    ],
  },

  // ========== SYSTEM ADMINISTRATION ==========
  {
    text: 'Settings',
    icon: <SettingsIcon />,
    permission: 'admin.users.read',
    children: [
      { text: 'Users', icon: <PeopleIcon />, path: '/admin/users', permission: 'admin.users.read' },
      { text: 'Roles', icon: <PartyIcon />, path: '/admin/roles', permission: 'admin.roles.read' },
      { text: 'Warehouses', icon: <WarehouseIcon />, path: '/admin/configuration/warehouses', permission: 'admin.system.config' },
      { text: 'Invoice Templates', icon: <ReceiptIcon />, path: '/admin/configuration/invoice-templates', permission: 'admin.system.config' },
    ],
  },

  // ========== INTEGRATIONS & AUTOMATION ==========
  {
    text: 'Integrations',
    icon: <WebhookIcon />,
    permission: 'integrations.read',
    children: [
      { text: 'Configuration', icon: <SettingsIcon />, path: '/admin/integrations/config', permission: 'integrations.read' },
      { text: 'Health Monitor', icon: <AssessmentIcon />, path: '/admin/integrations/health', permission: 'integrations.read' },
      { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/integrations/analytics', permission: 'integrations.read' },
      { text: 'Templates', icon: <AssignmentIcon />, path: '/admin/integrations/templates', permission: 'integrations.read' },
    ],
  },

  // ========== SECTION LABEL + EXTERNAL LINKS ==========
  {
    text: 'links-separator',
    icon: <></>,
    separator: true,
  },
  {
    text: 'LINKS & DOCS',
    icon: <></>,
    sectionLabel: true,
  },
  {
    text: 'API Documentation',
    icon: <ApiDocsIcon />,
    path: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/docs`,
    external: true,
  },
  {
    text: 'API Reference',
    icon: <AssessmentIcon />,
    path: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/redoc`,
    external: true,
  },
  {
    text: 'Customer Portal',
    icon: <CustomerPortalIcon />,
    path: import.meta.env.VITE_CUSTOMER_PORTAL_URL || '',
    external: true,
  },
]

/**
 * Quick Action Shortcuts for Top Bar
 * Most frequently used actions for quick access
 */
export const getQuickActions = () => [
  { text: 'New Sales Order', icon: <ShoppingCartIcon />, path: '/admin/documents/sales-orders/create', permission: 'orders.create' },
  { text: 'New Purchase Order', icon: <PurchaseIcon />, path: '/admin/purchase/orders/create', permission: 'orders.create' },
  { text: 'Take Payment', icon: <PaymentIcon />, path: '/admin/payments/create', permission: 'payments.process' },
  { text: 'New Customer', icon: <PeopleIcon />, path: '/admin/parties/customers/create', permission: 'customers.create' },
  { text: 'New Quotation', icon: <CreateDocIcon />, path: '/admin/documents/estimates/create', permission: 'orders.create' },
  { text: 'New Invoice', icon: <ReceiptIcon />, path: '/admin/documents/invoices/create', permission: 'orders.create' },
  { text: 'Add Product', icon: <AddIcon />, path: '/admin/products/create', permission: 'products.create' },
]

// getRolePermissions function removed. System now uses dynamic backend-driven RBAC.
